import React, { useMemo } from 'react';
import { 
  LineChart, 
  Line, 
  Area,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ReferenceLine,
  ResponsiveContainer,
  ComposedChart,
  Bar
} from 'recharts';
import { Box, Typography, useTheme, Paper, Chip } from '@mui/material';
import { format } from 'date-fns';

interface PriceData {
  date: Date;
  price: number;
  volume: number;
}

interface StockChartProps {
  symbol: string;
  data: PriceData[];
  pivotPrice?: number;
  currentPrice: number;
  priceChangePercent: number;
  lastUpdated?: Date;
  currentVolume?: number;
  volumeChgVs50Day?: number;
  volumeSpikeRatio?: number;
  tradingDayProgress?: number;
  dataDelay?: number; // Data delay in minutes
}

export const StockChart: React.FC<StockChartProps> = ({
  symbol,
  data,
  pivotPrice,
  currentPrice,
  priceChangePercent,
  lastUpdated,
  currentVolume,
  volumeChgVs50Day,
  volumeSpikeRatio,
  tradingDayProgress,
  dataDelay
}) => {
  const theme = useTheme();

  const chartData = useMemo(() => {
    const mapped = data.map(item => ({
      date: item.date.getTime(),
      dateDisplay: format(item.date, 'MMM d'),
      price: item.price,
      volume: item.volume || 0, // Volume in shares
      pivotPrice: pivotPrice
    }));
    // Debug: Check if volume data exists
    if (mapped.length > 0 && !mapped.some(d => d.volume > 0)) {
      console.warn(`No volume data found for ${symbol}`);
    }
    return mapped;
  }, [data, pivotPrice, symbol]);

  const minPrice = useMemo(() => {
    const prices = data.map(d => d.price);
    if (pivotPrice) prices.push(pivotPrice);
    return Math.min(...prices) * 0.95;
  }, [data, pivotPrice]);

  const maxPrice = useMemo(() => {
    const prices = data.map(d => d.price);
    if (pivotPrice) prices.push(pivotPrice);
    return Math.max(...prices) * 1.05;
  }, [data, pivotPrice]);

  const maxVolume = useMemo(() => {
    return Math.max(...data.map(d => d.volume)) * 1.2;
  }, [data]);

  const formatPrice = (value: number) => `$${value.toFixed(2)}`;
  const formatVolume = (value: number) => {
    if (value >= 1000000000) return `${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toString();
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {data.dateDisplay}
          </Typography>
          <Typography variant="body2" color="primary">
            Price: {formatPrice(data.price)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Volume: {data.volume.toLocaleString()}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{symbol}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h5" sx={{ fontWeight: 'medium' }}>
              ${currentPrice.toFixed(2)}
            </Typography>
            <Chip 
              label={`${priceChangePercent >= 0 ? '+' : ''}${priceChangePercent.toFixed(2)}%`}
              color={priceChangePercent >= 0 ? 'success' : 'error'}
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Box>
          {currentVolume !== undefined && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                {currentVolume.toLocaleString()}
              </Typography>
              {volumeChgVs50Day !== undefined && (
                <Chip 
                  label={`${volumeChgVs50Day >= 0 ? '+' : ''}${volumeChgVs50Day.toFixed(0)}% vs 50D`}
                  color={volumeChgVs50Day > 50 ? 'success' : volumeChgVs50Day > 0 ? 'default' : 'error'}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              )}
              {volumeSpikeRatio !== undefined && volumeSpikeRatio !== null && tradingDayProgress !== undefined && tradingDayProgress > 0 && tradingDayProgress < 1 && (
                <Chip 
                  label={`${(volumeSpikeRatio * 100).toFixed(0)}% pace @ ${(tradingDayProgress * 100).toFixed(0)}% day`}
                  color={volumeSpikeRatio >= 1.5 ? 'error' : volumeSpikeRatio >= 1.0 ? 'warning' : 'default'}
                  size="small"
                  sx={{ 
                    mt: 0.5,
                    backgroundColor: volumeSpikeRatio >= 1.5 ? theme.palette.error.main : undefined,
                    color: volumeSpikeRatio >= 1.5 ? theme.palette.error.contrastText : undefined
                  }}
                />
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            {lastUpdated && (
              <Typography variant="caption" color="text.secondary">
                Updated: {format(lastUpdated, 'MMM d, h:mm a')}
              </Typography>
            )}
            {dataDelay && (
              <Chip
                label={`${dataDelay}-min delayed`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          
          <XAxis 
            dataKey="dateDisplay"
            stroke={theme.palette.text.secondary}
            style={{ fontSize: 12 }}
          />
          
          <YAxis 
            yAxisId="price"
            orientation="left"
            domain={[minPrice, maxPrice]}
            tickFormatter={formatPrice}
            stroke={theme.palette.text.secondary}
            style={{ fontSize: 12 }}
          />
          
          <YAxis 
            yAxisId="volume"
            orientation="right"
            domain={[0, maxVolume]}
            tickFormatter={formatVolume}
            stroke={theme.palette.text.secondary}
            style={{ fontSize: 12 }}
          />
          
          <Tooltip content={<CustomTooltip />} />
          
          {pivotPrice && (
            <ReferenceLine
              yAxisId="price"
              y={pivotPrice}
              stroke={theme.palette.warning.main}
              strokeDasharray="5 5"
              strokeWidth={2}
              label={{
                value: `Pivot: $${pivotPrice.toFixed(2)}`,
                position: 'right',
                style: { 
                  fill: theme.palette.warning.main,
                  fontSize: 12,
                  fontWeight: 'medium'
                }
              }}
            />
          )}
          
          <Bar
            yAxisId="volume"
            dataKey="volume"
            fill={theme.palette.primary.main}
            opacity={0.2}
          />
          
          <Line
            yAxisId="price"
            type="monotone"
            dataKey="price"
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};